import { apiClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type {
  BulkAddSubsidiaryUsersRequest,
  CreateSubsidiaryRequest,
  SubsidiaryHealthCheck,
  UpdateSubsidiaryRequest,
} from '../types/subsidiary.types';
import type { Tenant } from '../types/tenant.types';

/* ------------------------------------------------------------------ */
/*  Subsidiary queries                                                 */
/* ------------------------------------------------------------------ */

/** List current user's subsidiaries (GET /subsidiaries). */
export async function listSubsidiaries(): Promise<Tenant[]> {
  const res = await apiClient
    .get(endpoints.subsidiaries.list())
    .json<Tenant[] | { data: Tenant[] }>();
  return Array.isArray(res) ? res : res.data;
}

/** Search all subsidiaries (super admin only — POST /subsidiaries/search). */
export async function searchSubsidiaries(query?: string): Promise<Tenant[]> {
  const res = await apiClient
    .post(endpoints.subsidiaries.search(), { json: { query } })
    .json<Tenant[] | { data: Tenant[] }>();
  return Array.isArray(res) ? res : res.data;
}

export async function getSubsidiary(id: string): Promise<Tenant> {
  return apiClient.get(endpoints.subsidiaries.byId(id)).json<Tenant>();
}

export async function getSubsidiaryHealth(): Promise<SubsidiaryHealthCheck> {
  return apiClient.get(endpoints.subsidiaries.health()).json<SubsidiaryHealthCheck>();
}

/* ------------------------------------------------------------------ */
/*  Subsidiary mutations                                               */
/* ------------------------------------------------------------------ */

/** Create a new subsidiary (POST /subsidiaries). Payload must include organizationId. */
export async function createSubsidiary(payload: CreateSubsidiaryRequest): Promise<Tenant> {
  return apiClient.post(endpoints.subsidiaries.create(), { json: payload }).json<Tenant>();
}

/** Update a subsidiary (PATCH /subsidiaries/{id}). Only name + status are updatable. */
export async function updateSubsidiary(
  id: string,
  payload: UpdateSubsidiaryRequest,
): Promise<Tenant> {
  return apiClient.patch(endpoints.subsidiaries.update(id), { json: payload }).json<Tenant>();
}

export async function deleteSubsidiary(id: string): Promise<void> {
  await apiClient.delete(endpoints.subsidiaries.delete(id));
}

/** Bulk-add existing platform users to a subsidiary (POST /subsidiaries/{id}/users/add/bulk). */
export async function bulkAddSubsidiaryUsers(
  subsidiaryId: string,
  payload: BulkAddSubsidiaryUsersRequest,
): Promise<void> {
  await apiClient.post(endpoints.subsidiaries.bulkAddMembers(subsidiaryId), { json: payload });
}
